import { DimSumEnhancedNode } from '@amzn/gft-dimsum-service';
import { EnhancedComment } from '@deckManagement/components/Comments/CommentsDrawer.types';
import { Deckline, KPIDrivers } from '@services/api/deckManagement/graphql/schema/API';
import { ReactNode } from 'react';
import { CommonEntityMetadata } from 'types/Common.types';
import { DriverDisplayOptionMap } from 'types/PageContext.types';

import { DecklineRowData, PartialKPIDrivers } from './Page.types';
import { KpiDimension, PageSidePanelDropDown } from './PageSidePanel.types';

/**
 * Enhanced Deckline Interface
 *
 * Extends the base Deckline and adds Common metadata elements that aid in change tracking and entity creation
 * @interface
 */
export interface EnhancedDeckline extends Omit<Deckline, 'pk' | 'sk'>, CommonEntityMetadata<EnhancedDeckline> {
  // Optional here, since new Deckline entities may not immediately have this
  pk?: string;
  // Optional here, since new Deckline entities may not immediately have this
  sk?: string;
  // Generated Deckline ID stored as needed for Deckline entries.
  deckline_id?: string;
  // Stores the start index for rows that track Subtotal
  subtotal_start_index?: string;
  // Stores the end index for rows that track Subtotal
  subtotal_end_index?: string;
  // Comments (using EnhancedComment which includes metadata)
  comments?: Array<EnhancedComment | null> | null;
  // Stores the actual kpi name (without footnote identifiers)
  actual_kpi_name?: string;
  // Stores decklineType
  deckline_type?: PageSidePanelDropDown;
  // Page ID
  page_id?: string;
}

/**
 * Delete Deckline Data
 *
 * Interface for the data required to delete a Deckline
 * @interface
 */
export type DeleteDecklineData = Pick<EnhancedDeckline, 'page_id' | 'deckline_id'>;

/**
 * Enum for new deckline insertion position
 */
export enum NewDecklinePosition {
  ABOVE = 'above',
  BELOW = 'below',
}

/**
 * Interface for the Decklines Context Type
 *
 * @interface DecklinesContextType
 * @property {Array<EnhancedDeckline>} pageDecklines - Uses UseGetDecklines to get all Decklines for a Page Granularity
 * @property {Record<string, DimSumEnhancedNode>} resolvedNodes - Resolved DimSum nodes for RevGP data
 * @property {function} updatePageDeckline - Function to update a page Deckline by field and value
 * @property {function} insertPageDeckline - Function to insert a new page Deckline at a specified position
 * @property {function} deletePageDeckline - Function to delete a page Deckline
 * @property {EnhancedDeckline | null} selectedDeckline - Currently selected deckline
 * @property {DriverDisplayOptionMap} selectedDecklineDriverOptions - Driver display options for the selected deckline
 * @property {function} chooseDeckline - Function to select a deckline
 * @property {function} saveDecklineChanges - Function to save all Deckline changes
 * @property {boolean} isSaving - Saving state for SaveDeck operation from Page View
 * @property {boolean} isLoading - Loading state for UseGetDecklines and node resolution
 * @property {function} saveSubtotal - Function to save the subtotal for a deckline
 * @property {boolean} isError - Error state for UseGetDecklines
 * @property {Error | null} error - Error object for UseGetDecklines
 */
export interface DecklinesContextType {
  pageDecklines: Array<EnhancedDeckline>; // Uses UseGetDecklines to get all Decklines for a Page Granularity
  resolvedNodes: Record<string, DimSumEnhancedNode | null>; // Resolved DimSum nodes for RevGP data
  subtotalMap: Map<string, DecklineRowData[]>; // Map of subtotal parent to child decklines
  subtotalRanges: Map<string, { min: number; max: number }>; // Min/max row ranges for subtotals
  setSubtotalMap: (map: Map<string, DecklineRowData[]>) => void;
  setSubtotalRanges: (ranges: Map<string, { min: number; max: number }>) => void;
  updatePageDeckline: <K extends keyof EnhancedDeckline>(
    internalDecklineId: string,
    field: K,
    newValue: EnhancedDeckline[K],
  ) => void;
  /**
   * Handles updating deckline drivers when Page Drivers updates.
   * @param pageDriverPrevious - The previous state of Page Drivers. Use this to compare current values in deckline drivers.
   * @param updates - The updated values which need to be applied to the decklines.
   */
  updateDecklinesForPageDriverChanges: (pageDriverPrevious: KPIDrivers, updates: PartialKPIDrivers) => void;
  insertPageDeckline: (targetDecklineId?: string, position?: NewDecklinePosition) => void;
  deletePageDeckline: (targetDecklineId: string) => void;
  selectedDeckline: EnhancedDeckline | null;
  selectedDecklineDriverOptions: DriverDisplayOptionMap | null;
  /**
   * Updates the active state of the selected deckline driver.
   * @param kpiDimension
   * @param active
   */
  updateDecklineDriverActiveState: (kpiDimension: KpiDimension, active: boolean) => void;
  /**
   * Updates the active state of the selected deckline denominator driver.
   * @param kpiDimension
   * @param active
   */
  updateDecklineDenominatorDriverActiveState: (kpiDimension: KpiDimension, active: boolean) => void;
  selectedDenominatorDriverOptions: DriverDisplayOptionMap | null;
  decklineType: PageSidePanelDropDown;
  setDecklineType: (type: PageSidePanelDropDown) => void;
  updateAllDecklinesDenominatorType: (newType: PageSidePanelDropDown) => void;
  denominatorType?: PageSidePanelDropDown;
  setDenominatorType: (type: PageSidePanelDropDown) => void;
  chooseDeckline: (deckline: EnhancedDeckline | null) => void;
  repositionDeckline: (targetDecklineId: string, afterDecklineId?: string | null) => void;
  isCreatingSubtotal: boolean;
  setIsCreatingSubtotal: (isCreating: boolean) => void;
  saveDecklineChanges: (successCallback?: () => void, errorCallback?: () => void) => void;
  handleSave: (successCallback?: () => void, errorCallback?: () => void) => void;
  isSaving: boolean;
  saveSubtotal: (enhancedDeckline: EnhancedDeckline[], successCallback?: () => void) => void;
  updateDecklineComments: (decklineId: string, comments: EnhancedComment[]) => void;
  isLoading: boolean;
  isError: boolean;
  error: Error | null;
}

/**
 * Interface for the Decklines Provider Props
 *
 * @interface DecklinesProviderProps
 * @property {ReactNode} children - React children of the Context
 */
export interface DecklinesProviderProps {
  children?: ReactNode;
}
